import { Badge, Button } from "@izicvs/ui";
import { cn } from "@izicvs/utils";
import { t } from "@lingui/macro";
import {
  ArrowSquareOut,
  CheckCircle,
  CircleNotch,
  Kanban,
  MapPin,
  PaperPlaneTilt,
  Sparkle,
} from "@phosphor-icons/react";

import type { JobListing } from "@/client/services/jobs";

export const fitVariant = (fit: number) => {
  if (fit >= 70) return "success";
  if (fit >= 45) return "warning";
  return "secondary";
};

type Props = {
  job: JobListing;
  fit?: number | null;
  tracked?: boolean;
  preparing?: boolean;
  onAnalyze: (job: JobListing) => void;
  onTrack: (job: JobListing) => void;
  onPrepare: (job: JobListing) => void;
};

export const JobCard = ({ job, fit, tracked, preparing, onAnalyze, onTrack, onPrepare }: Props) => (
  <div
    className={cn(
      "flex flex-col gap-y-3 rounded-2xl border bg-background p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md",
      tracked && "border-success/40 bg-success/5",
    )}
  >
    <div className="flex items-start justify-between gap-x-3">
      <div className="min-w-0">
        <h3 className="line-clamp-2 font-semibold">{job.title}</h3>
        <p className="mt-0.5 text-sm opacity-75">{job.company}</p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-y-1">
        {fit != null && (
          <Badge variant={fitVariant(fit)} className="font-bold">
            {t`${fit}% match`}
          </Badge>
        )}
        <Badge variant="secondary" className="capitalize">
          {job.source}
        </Badge>
      </div>
    </div>

    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs opacity-70">
      {job.location && (
        <span className="flex items-center gap-x-1">
          <MapPin size={13} />
          {job.location}
        </span>
      )}
      {job.remote && (
        <Badge outline variant="info">
          {t`Remote`}
        </Badge>
      )}
      {job.salary && <span>{job.salary}</span>}
    </div>

    {job.description && <p className="line-clamp-2 text-sm opacity-80">{job.description}</p>}

    {job.tags.length > 0 && (
      <div className="flex flex-wrap gap-1.5">
        {job.tags.slice(0, 5).map((tag) => (
          <Badge key={tag} variant="secondary" className="text-xs font-normal">
            {tag}
          </Badge>
        ))}
      </div>
    )}

    <div className="mt-auto flex flex-wrap gap-2 pt-1">
      <Button
        size="sm"
        onClick={() => {
          onAnalyze(job);
        }}
      >
        <Sparkle weight="fill" className="mr-2" />
        {t`Analyze with my resume`}
      </Button>
      {tracked ? (
        <Button disabled size="sm" variant="secondary">
          <CheckCircle weight="fill" className="mr-2 text-success" />
          {t`Tracked`}
        </Button>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            onTrack(job);
          }}
        >
          <Kanban className="mr-2" />
          {t`Track`}
        </Button>
      )}
      {job.source === "greenhouse" && (
        <Button
          size="sm"
          variant="secondary"
          disabled={preparing}
          onClick={() => {
            onPrepare(job);
          }}
        >
          {preparing ? (
            <CircleNotch className="mr-2 animate-spin" />
          ) : (
            <PaperPlaneTilt className="mr-2" />
          )}
          {t`Prepare application`}
        </Button>
      )}
      <Button asChild size="sm" variant="ghost">
        <a href={job.url} target="_blank" rel="noreferrer noopener">
          <ArrowSquareOut className="mr-2" />
          {t`View offer`}
        </a>
      </Button>
    </div>
  </div>
);

export const JobCardSkeleton = () => (
  <div className="flex flex-col gap-y-3 rounded-2xl border bg-background p-4 shadow-sm">
    <div className="flex items-start justify-between gap-x-3">
      <div className="w-full space-y-2">
        <div className="h-4 w-3/4 rounded bg-secondary/60" />
        <div className="h-3 w-1/3 rounded bg-secondary/50" />
      </div>
      <div className="h-5 w-16 shrink-0 rounded-full bg-secondary/50" />
    </div>
    <div className="h-3 w-1/2 rounded bg-secondary/40" />
    <div className="space-y-1.5">
      <div className="h-3 w-full rounded bg-secondary/40" />
      <div className="h-3 w-5/6 rounded bg-secondary/40" />
    </div>
    <div className="mt-1 flex gap-2">
      <div className="h-8 w-40 rounded-full bg-secondary/50" />
      <div className="h-8 w-20 rounded-full bg-secondary/40" />
    </div>
  </div>
);
